import React from 'react';
import styled, { css, StyledComponent } from 'styled-components';
import { COLOR, ie, mobile, STYLE } from '../../../styles/variables';
import { Position } from '../../../config/models';

export type SlideType<
    Tag extends keyof JSX.IntrinsicElements | React.ComponentType<any> = 'div',
    TriggerConfig = {},
    AddOns = {}
> = StyledComponent<Tag, any, DescriptionProps<TriggerConfig> & AddOns>;

export const getPosition = (position?: Position) => {
    switch (position) {
        case Position.LEFT:
            return css`
                margin-right: auto;
                margin-left: 0;
            `;
        case Position.RIGHT:
            return css`
                margin-right: 0;
                margin-left: auto;
            `;
        default:
            return css`
                margin-right: auto;
                margin-left: auto;
            `;
    }
};

export const Wrapper = styled.div<{ width?: string }>`
    width: ${({ width }) => `${width || '100%'}`};
    height: 100%;
    ${getPosition()};

    @media ${mobile} {
        width: 100%;
    }
`;

export const SlidesWrapper = styled.div<{ position?: Position; width?: string }>`
    width: ${({ width }) => `${width || `calc(100% - ${STYLE.content.margin.right})`}`};
    margin: 0 auto;
    z-index: 999;
    backface-visibility: hidden;
    transform: translate3d(0, 0, 0);
    pointer-events: none;
    max-width: ${STYLE.content.maxWidth};
    ${({ position }) => getPosition(position)};

    @media ${mobile} {
        width: 85%;
        ${getPosition()};
    }

    @media ${ie} {
        padding-top: 100vh;
    }

    > div {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const Sticky = styled.div<{
    useFullHeight?: boolean;
    position?: Position;
    navbarHeight?: string;
    width?: string;
}>`
    position: sticky;
    top: ${({ useFullHeight, navbarHeight }) => (useFullHeight ? navbarHeight : `calc(${navbarHeight} + 28px)`)};
    width: ${({ width }) => `${width || '100%'}`};
    height: ${({ useFullHeight }) => (useFullHeight ? '100%' : 'auto')};
    margin: 0 auto;
    transform: translate3d(0, 0, 0);
    transform-style: preserve-3d;
    backface-visibility: hidden;
    overflow: hidden;
    ${({ position }) => getPosition(position)};

    @media ${mobile} {
        width: 100%;
        ${getPosition()};
    }

    @media ${ie} {
        overflow: visible;

        &.js-is-stuck ~ ${SlidesWrapper} {
            overflow: hidden;
            height: 0;
        }
    }
`;

export const Padding = styled.div<{ height?: string | number; lineWidth?: number; lineColor?: string }>`
    padding-bottom: ${({ height = '20px' }) => (typeof height === 'number' ? `${height}px` : height)};
    pointer-events: none;

    ${({ lineWidth, lineColor }) =>
        lineWidth
            ? `
   :not(:first-child):not(:last-child) {
        width: calc(50% - ${lineWidth}px);
        border-right: ${lineWidth}px solid ${lineColor || 'white'};
    }
  `
            : ''};

    @media ${mobile} {
        border-right: none !important;
    }
`;

export enum Shadow {
    HARD = '5px 5px 0px ${COLOR.secondary}',
    SOFT = '5px 5px 10px ${COLOR.secondary}'
}

export type DescriptionProps<T = any> = {
    position?: Position;
    innerPadding?: string;
    isVisible?: boolean;
    isClickable?: boolean;
    bgColor?: string;
    fullWidth?: boolean;
    textColor?: string;
    trigger?: T;
    shadowColor?: string;
    lineWidth?: number;
    lineColor?: string;
    trackingId?: string;
    shadow?: Shadow;
    /**
     * Disables Padding after the element
     */
    noPadding?: boolean;
    /**
     * Sets the padding after the element
     */
    paddingHeight?: string;
    height?: string;
};

export const Description = styled.div<DescriptionProps>`
    padding: ${({ innerPadding }) => innerPadding || '1.15rem'};
    color: ${({ textColor }) => textColor || 'black'};
    background: ${({ bgColor }) => bgColor || 'white'};
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
    z-index: 10;
    max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '80%')};
    hyphens: auto;
    box-shadow: ${({ shadow, shadowColor }) =>
        shadow ? `5px 5px ${shadow === Shadow.SOFT ? 10 : 0}px ${shadowColor || COLOR.secondary}` : 'none'};
    opacity: ${({ isVisible = true }) => (isVisible ? 1 : 0)};
    transition: opacity 0.5s ease;
    pointer-events: ${({ isClickable }) => (isClickable ? 'all' : 'none')};
    ${({ position }) => getPosition(position)};
    ${({ lineWidth }) => (lineWidth ? 'margin: 2em 0' : '')};

    > *:first-child {
        margin-top: 0;
    }

    > *:last-child {
        margin-bottom: 0;
    }

    > h4 {
        margin-bottom: 0.4rem;
        margin-top: 0;
    }
    transform: translate3d(0, 0, 0);
    height: ${({ height = 'auto' }) => height};

    @media ${mobile} {
        max-width: unset;
        opacity: 1;
        margin: 0;
        box-shadow: none;
    }
`;

// @ts-ignore
export const Empty = styled(Description)`
    width: 100%;
    background: transparent;
    pointer-events: none;
    height: ${({ height }) => (height ? height : 0)};
`;

export const AbsoluteWrapper = styled.div`
    position: absolute;
    left: 50%;

    @media ${mobile} {
        width: 85%;
    }
`;

export const AbsoluteInner = styled.div`
    position: relative;
    left: -50%;
    transform: translate3d(0, 0, 0);
    text-align: center;
`;
